import React, { useContext, useEffect, useState } from "react";
import { Chip, Chips } from "@mantine/core";
import { Navigate } from "react-router-dom";
import Tag from "../../tags/services/model/Tag";
import { queryClient } from "../../../App";
import { AuthContext } from "../../../providers/AuthProvider";
import { HttpClientError } from "../../../providers/HttpClient";
import useToggleBulkAssigneeTagApiService from "../../tags/services/api/ToggleBulkAssigneeTagApiService";
import Employee from "../services/model/Employee";
import { useToasts } from "../../../providers/notifications/ToastsContext";

type EmployeeTableTagDropdownProps = {
  employees: Employee[];
  activePage: number;
  tags: Tag[];
};

const EmployeeTagChip: React.FC<EmployeeTableTagDropdownProps> = ({
  employees,
  tags,
  activePage,
}) => {
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();
  const { isError, isSuccess, error, isLoading, mutate } =
    useToggleBulkAssigneeTagApiService();
  const [assignedTags, setAssignedTags] = useState<string[]>([]);
  const [mode, setMode] = useState("");

  useEffect(() => {
    if (isError) {
      showToast({
        strapiId: "tag_action_failed",
        data: {
          mode,
          message: HttpClientError(error),
        },
      });
    }
  }, [isError]);

  useEffect(() => {
    if (isSuccess) {
      queryClient
        .invalidateQueries([
          "EmployeeListApiService",
          ...Object.values({
            employerId: auth?.employer.id ?? 0,
            pagination: false,
            page: activePage,
            tsAndCsAccepted: false,
          }),
        ])
        .then(() => {
          showToast({
            strapiId: "tag_action_success",
            data: {
              mode,
            },
          });
        });
    }
  }, [isSuccess]);

  useEffect(() => {
    const array =
      employees.length > 0
        ? ([] as number[]).concat(...employees.map((it) => it.tags))
        : [];
    setAssignedTags(Array.from(new Set(array)).map((it) => it.toString()));
  }, [employees]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <Chips
      onChange={(value) => {
        let tagId = value.filter((it) => !assignedTags.includes(it));
        if (tagId && tagId.length > 0) {
          setMode("assigned");
          mutate({
            tagId: Number(tagId[0]),
            employees: employees.map((it) => it.id),
            isTagged: true,
          });
        } else {
          tagId = assignedTags.filter((it) => !value.includes(it));
          if (tagId && tagId.length > 0) {
            setMode("unassigned");
            mutate({
              tagId: Number(tagId[0]),
              employees: employees.map((it) => it.id),
              isTagged: false,
            });
          }
        }
        setAssignedTags(value);
      }}
      multiple
      value={assignedTags.map((it) => it.toString())}
    >
      {tags.map((it) => (
        <Chip
          disabled={isLoading}
          key={`chip-${it.id}`}
          value={it.id.toString()}
        >
          {it.name}
        </Chip>
      ))}
    </Chips>
  );
};

export default EmployeeTagChip;
